<template>
  <page>
    <div slot="pageAction">
      <div class="actionBlock">
        <v-menu offset-y :min-width="250">
          <template v-slot:activator="{ on }">
            <v-btn text color="secondary" v-on="on">
              Related Tasks
              <v-icon right>mdi-link</v-icon>
            </v-btn>
          </template>
          <side-function-menu :items="sideFunction" @doClick="$router.push($event)"></side-function-menu>
        </v-menu>
      </div>
    </div>
    <lazy-div :load="doInit">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon v-if="newMode">mdi-plus</v-icon>
          <v-icon v-else-if="reviewMode">mdi-pencil</v-icon>
          <v-icon v-else-if="queryMode">mdi-magnify</v-icon>
          <v-toolbar-title v-if="newMode">Create New Package Plan</v-toolbar-title>
          <v-toolbar-title v-else-if="reviewMode">Edit Package Plan</v-toolbar-title>
          <v-toolbar-title v-else-if="queryMode">Query Package Plan</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container fluid grid-list-lg>
            <v-layout row wrap>
              <v-flex xs6 v-if="newMode">
                <v-autocomplete
                  :items="scheme"
                  v-model="pkgPlanForm.scheme"
                  :label="pkgPlanLabel.scheme"
                  item-text="label"
                  item-value="value"
                  :error-messages="errors.collect('scheme')"
                  data-vv-name="scheme"
                  :data-vv-as="pkgPlanLabel.scheme"
                  v-validate="'required'"
                  @input="doGetPolType($event)"
                  clearable
                ></v-autocomplete>
              </v-flex>
              <v-flex xs6 v-if="newMode">
                <v-text-field
                  v-model="pkgPlanForm.planNo"
                  :label="pkgPlanLabel.planNo"
                  :error-messages="errors.collect('planNo')"
                  data-vv-name="planNo"
                  :data-vv-as="pkgPlanLabel.planNo"
                  v-validate="'required|max:4'"
                ></v-text-field>
              </v-flex>

              <!-- Review -->
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ pkgPlanLabel.scheme }}</v-subheader>
                <div class="queryValue" v-for="item in filterSchemeList" :key="item.label">{{ item.label }}</div>
              </v-flex>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ pkgPlanLabel.planNo }}</v-subheader>
                <div class="queryValue">{{ pkgPlanForm.planNo }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <v-text-field
                  v-model="pkgPlanForm.seqNo"
                  :label="pkgPlanLabel.seqNo"
                  :error-messages="errors.collect('pkgPlanForm.seqNo')"
                  data-vv-name="pkgPlanForm.seqNo"
                  :data-vv-as="pkgPlanLabel.seqNo"
                  v-validate="'required|numeric|mustGreaterThenZero'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.seqNo }}</v-subheader>
                <div class="queryValue">{{ pkgPlanForm.seqNo }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <v-text-field
                  v-model="pkgPlanForm.planDesc"
                  :label="pkgPlanLabel.planDesc"
                  :error-messages="errors.collect('pkgPlanForm.planDesc')"
                  data-vv-name="pkgPlanForm.planDesc"
                  :data-vv-as="pkgPlanLabel.planDesc"
                  v-validate="'required|max:40'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.planDesc }}</v-subheader>
                <div class="queryValue">{{ pkgPlanForm.planDesc }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <cal-picker
                  v-model="pkgPlanForm.effDate"
                  :label="pkgPlanLabel.effDate"
                  :data-vv-as="pkgPlanLabel.effDate"
                  data-vv-name="pkgPlanForm.effDate"
                  :error-messages="errors.collect('pkgPlanForm.effDate')"
                  v-validate="'required'"
                ></cal-picker>
              </v-flex>
              <v-flex v-else>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.effDate }}</v-subheader>
                <div class="queryValue">{{ convertDate(pkgPlanForm.effDate) }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <cal-picker
                  v-model="pkgPlanForm.expDate"
                  :label="pkgPlanLabel.expDate"
                  :data-vv-as="pkgPlanLabel.expDate"
                  data-vv-name="pkgPlanForm.expDate"
                  :error-messages="errors.collect('pkgPlanForm.expDate')"
                  v-validate="expDateValidation"
                ></cal-picker>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.expDate }}</v-subheader>
                <div class="queryValue">{{ convertDate(pkgPlanForm.expDate) }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <v-text-field
                  ref="childDiscPct"
                  v-model="pkgPlanForm.childDiscPct"
                  :label="pkgPlanLabel.childDiscPct"
                  v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                  :error-messages="errors.collect('pkgPlanForm.childDiscPct')"
                  data-vv-name="pkgPlanForm.childDiscPct"
                  :data-vv-as="pkgPlanLabel.childDiscPct"
                  @blur="setValue()"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.childDiscPct }}</v-subheader>
                <div class="queryValue">{{ pkgPlanForm.childDiscPct }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <v-text-field
                  v-model="pkgPlanForm.noAdultChildDisc"
                  :label="pkgPlanLabel.noAdultChildDisc"
                  :error-messages="errors.collect('pkgPlanForm.noAdultChildDisc')"
                  data-vv-name="pkgPlanForm.noAdultChildDisc"
                  :data-vv-as="pkgPlanLabel.noAdultChildDisc"
                  v-validate="'numeric'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.noAdultChildDisc }}</v-subheader>
                <div class="queryValue">{{ pkgPlanForm.noAdultChildDisc }}</div>
              </v-flex>
            </v-layout>

            <!-- Hidden Field for PolType -->
            <v-layout row wrap v-if="show">
              <v-flex xs6 v-if="show">
                <v-autocomplete
                  :items="poltype_scheme"
                  v-model="pkgPlanForm.polType"
                  :label="pkgPlanLabel.polType"
                  item-text="label"
                  item-value="value"
                  :error-messages="errors.collect('pkgPlanForm.polType')"
                  data-vv-name="pkgPlanForm.polType"
                  :data-vv-as="pkgPlanLabel.polType"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  v-model="pkgPlanForm.polType"
                  :label="pkgPlanLabel.polType"
                  :error-messages="errors.collect('pkgPlanForm.polType')"
                  data-vv-name="pkgPlanForm.polType"
                  :data-vv-as="pkgPlanLabel.polType"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.polType }}</v-subheader>
                <div class="queryValue">{{ pkgPlanForm.polType }}</div>
              </v-flex>
            </v-layout>
            <v-layout>
              <v-flex xs6 v-if="!queryMode">
                <v-text-field
                  v-model="pkgPlanForm.minPolPd"
                  :label="pkgPlanLabel.minPolPd"
                  :error-messages="errors.collect('pkgPlanForm.minPolPd')"
                  data-vv-name="pkgPlanForm.minPolPd"
                  :data-vv-as="pkgPlanLabel.minPolPd"
                  v-validate="'required'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.minPolPd }}</v-subheader>
                <div class="queryValue">{{ pkgPlanForm.minPolPd }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.createdBy }}</v-subheader>
                <div class="queryValue">{{ pkgPlanForm.createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.createdDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(pkgPlanForm.createdDate) }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-if="!newMode">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ pkgPlanForm.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ pkgPlanLabel.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(pkgPlanForm.modifiedDate) }}</div>
              </v-flex>
            </v-layout>
            <v-spacer class="mt-3"></v-spacer>
            <v-layout>
              <v-btn color="red darken-1" dark class=" mr-2" @click.native="doCancel">
                Cancel
                <v-icon right dark>mdi-cancel</v-icon>
              </v-btn>
              <v-btn v-if="!queryMode" color="primary" dark class=" mr-2" @click="doSave" :loading="submitting">
                Save
                <v-icon right>mdi-content-save</v-icon>
              </v-btn>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </lazy-div>
  </page>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { commonAjaxMixin } from '@/mixins/common/commonAjaxMixin'
import { expandURI } from '@/utils/ajax'
import { formatDisplayDate, removeTime } from '@/utils/date'
import isAfter from 'date-fns/isAfter'
import { removeFormatNumber } from '@/utils/number.js'
import { formatPrice } from '@/utils/index'
import SideFunctionMenu from '@/components/core/claimManagement/sideFunction/SideFunctionMenu'

export default {
  data() {
    return {
      show: false,
      submitting: false,
      newMode: false,
      reviewMode: false,
      queryMode: false,
      scheme: [],
      poltype_scheme: [],
      pkgPlanLabel: {
        scheme: 'Scheme',
        seqNo: 'Sequence No.',
        planNo: 'Plan No.',
        planDesc: 'Plan Description',
        effDate: 'Effective Date',
        expDate: 'Expiry Date',
        childDiscPct: 'Child Discount %',
        noAdultChildDisc: 'Minimum No. of Adult for Child Discount',
        polType: 'Policy Type',
        maxBenNo: 'Maximum Benefit No.',
        minPolPd: 'Minimum Premium',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      pkgPlanForm: {
        scheme: null,
        seqNo: '0',
        planNo: null,
        planDesc: null,
        effDate: null,
        expDate: null,
        childDiscPct: '0.00',
        noAdultChildDisc: '0',
        polType: null,
        maxBenNo: null,
        minPolPd: 0.00,
        createdBy: this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName,
        createdDate: new Date(),
        modifiedBy: null,
        modifiedDate: null
      },
      loading: true,
      today: new Date(),
      sideFunction: [
        {
          text: 'Annual Renewal Bonus',
          icon: 'mdi-launch',
          iconColor: 'primary',
          params: { name: 'arbList', params: { incomingScheme: 'noScheme', incomingPlanNo: 'noPlanNo' } }
        },
        {
          text: 'Insured Age Band',
          icon: 'mdi-launch',
          iconColor: 'primary',
          params: { name: 'insdAgeBandList', params: { incomingScheme: 'noScheme', incomingPlanNo: 'noPlanNo' } }
        },
        {
          text: 'Package Risk',
          icon: 'mdi-launch',
          iconColor: 'primary',
          params: { name: 'pkgRiskList', params: { incomingScheme: 'noScheme', incomingPlanNo: 'noPlanNo', incomingPolType: 'noPolType' } }
        }
      ]
    }
  },
  methods: {
    async doInit() {
      await this.$store.dispatch('packagePlanInit')
      await Promise.all([this.$store.dispatch('commonsInit')])
      this.getCommonDataList('scheme')
      if (this.newPkgPlan) {
        this.pkgPlanForm.effDate = new Date()
        this.pkgPlanForm.expDate = this.pkgPlanForm.effDate
      } else {
        this.fetchData(true)
      }
    },
    async fetchData() {
      const apiURI = this.menu.getPkgPlan
      if (apiURI) {
        try {
          const expand = {
            scheme: this.pkgPlanId.scheme,
            planNo: this.pkgPlanId.planNo
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            this.pkgPlanForm.scheme = data.pkgPlan.scheme
            this.pkgPlanForm.seqNo = data.pkgPlan.seqNo
            this.pkgPlanForm.planNo = data.pkgPlan.planNo
            this.pkgPlanForm.planDesc = data.pkgPlan.planDesc
            this.pkgPlanForm.effDate = data.pkgPlan.effDate
            this.pkgPlanForm.expDate = data.pkgPlan.expDate
            this.pkgPlanForm.childDiscPct = formatPrice(data.pkgPlan.childDiscPct)
            this.pkgPlanForm.noAdultChildDisc = data.pkgPlan.noAdultChildDisc
            this.pkgPlanForm.polType = data.pkgPlan.polType
            this.pkgPlanForm.minPolPd = data.pkgPlan.minPolPd
            this.pkgPlanForm.createdBy = data.createdBy
            this.pkgPlanForm.createdDate = data.createdDate
            this.pkgPlanForm.modifiedBy = data.modifiedBy
            this.pkgPlanForm.modifiedDate = data.modifiedDate

            for (var i = 0; i < this.sideFunction.length; i++) {
              if (this.sideFunction[i].params.name === 'pkgRiskList') {
                this.sideFunction[i].params.params.incomingScheme = data.pkgPlan.scheme
                this.sideFunction[i].params.params.incomingPlanNo = data.pkgPlan.planNo
                this.sideFunction[i].params.params.incomingPolType = data.pkgPlan.polType
                this.sideFunction[i].params.params.incomingPlanDesc = data.pkgPlan.planDesc
                this.sideFunction[i].params.params.incomingSchemeDesc = data.schemeDesc
                this.sideFunction[i].params.params.incomingPolTypeDesc = data.polTypeDesc
              } else if (this.sideFunction[i].params.name === 'arbList') {
                this.sideFunction[i].params.params.incomingScheme = data.pkgPlan.scheme
                this.sideFunction[i].params.params.incomingPlanNo = data.pkgPlan.planNo
              } else if (this.sideFunction[i].params.name === 'insdAgeBandList') {
                this.sideFunction[i].params.params.incomingScheme = data.pkgPlan.scheme
                this.sideFunction[i].params.params.incomingPlanNo = data.pkgPlan.planNo
              }
            }
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doGetPolType(event) {
      if (this.pkgPlanForm.scheme !== '') {
        this.loading = true
        this.poltype_scheme = []
        this.$validator.validate('scheme').then((result) => {
          if (result) {
            const apiURI = this.menu.getPolTypeByScheme
            if (apiURI) {
              const expand = {
                scheme: this.pkgPlanForm.scheme
              }
              this.$http
                .get(expandURI(apiURI, expand))
                .then((result) => {
                  const { data } = result.data
                  if (data) {
                    data.filter((list) => {
                      this.poltype_scheme.push({ value: list.value, label: list.label })
                      this.pkgPlanForm.polType = list.value
                    })
                  } else {
                    this.loading = true
                    this.poltype_scheme = []
                  }
                })
                .catch((err) => {
                  this.poltype_scheme = []
                  const { data } = err.response
                  if (data) {
                    this.fieldMessage(data)
                  }
                })
            }
          } else {
            this.pkgPlanForm.polType = null
          }
        })
      }
    },
    doSave() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newPkgPlan) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doCreate() {
      const apiURI = this.menu.createPkgPlan
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.pkgPlanForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.$router.push({ name: 'pkgPlan' })
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doUpdate() {
      const apiURI = this.menu.updatePkgPlan
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.put(apiURI, this.pkgPlanForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.$router.push({ name: 'pkgPlan' })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doCancel() {
      this.$router.push({ name: 'pkgPlan' })
    },
    convertDate(date) {
      if (date !== null) {
        return formatDisplayDate(date)
      }
    },
    formatDisplayDate(xDate) {
      if (xDate === null) {
        return undefined
      } else {
        return formatDisplayDate(xDate)
      }
    },
    setValue() {
      this.$refs.childDiscPct.$el.cleave.setRawValue(this.pkgPlanForm.childDiscPct)
      this.pkgPlanForm.childDiscPct = formatPrice(this.$refs.childDiscPct.$el.cleave.getRawValue())
    }
  },
  created() {
    const jsonData = JSON.parse(sessionStorage.getItem('pkgPlan'))
    this.$store.commit('PACKAGEPLAN_COMMIT_KEY', jsonData)
    if (this.pkgPlanId.tranMode !== '') {
      if (this.pkgPlanId.tranMode === 'New') {
        this.newMode = true
      } else if (this.pkgPlanId.tranMode === 'Review') {
        this.reviewMode = true
      } else if (this.pkgPlanId.tranMode === 'Query') {
        this.queryMode = true
      }
    }

    this.$validator.extend('valExpiry', {
      getMessage(field, args) {
        return 'Expiry Date must be equal or greater than Effective Date'
      },
      validate(value, args) {
        if (args === void 0) args = []
        var expDate = args[0]
        if (expDate) {
          return !isAfter(removeTime(expDate), removeTime(value))
        }
      }
    })
    const mustGreaterThenZero = {
      getMessage: (field, args) => {
        return field + ' cannot be 0'
      },
      validate: (value, args) => {
        return removeFormatNumber(value) >= 0.000001
      }
    }

    this.$validator.extend('mustGreaterThenZero', mustGreaterThenZero)
  },
  computed: {
    menu() {
      return this.$store.state.pkgParam.pkgPlan.menu
    },
    newPkgPlan() {
      return this.pkgPlanId.tranMode === 'New'
    },
    pkgPlanId() {
      return this.$store.state.pkgParam.pkgPlan.pkgPlanId
    },
    expDateValidation() {
      if (this.pkgPlanForm.expDate !== '' && this.pkgPlanForm.expDate !== null) {
        return 'entry|valExpiry:' + this.pkgPlanForm.effDate
      } else {
        return ''
      }
    },
    filterSchemeList() {
      return this.scheme.filter((list) => {
        return list.value === this.pkgPlanForm.scheme
      })
    },
    filterPolTypeList() {
      return this.poltype_scheme.filter((list) => {
        return list.value === this.pkgPlanForm.polType
      })
    }
  },
  components: {
    SideFunctionMenu
  },
  mixins: [MessageMixin, commonAjaxMixin]
}
</script>
